import {
    Badge,
    Button,
    Drawer,
    EmptyState,
    Logo,
    Select,
    Sidebar,
    SidebarBrand,
    SidebarFooter,
    SidebarGroup,
    SidebarItem,
    SidebarNav,
    ThemeToggle,
} from '@platform/ui';
import { Link, Outlet, useMatches, useRouter, useRouterState } from '@tanstack/react-router';
import {
    Activity,
    Building,
    ChartColumn,
    CreditCard,
    FileText,
    House,
    Inbox,
    Landmark,
    Lock,
    LogOut,
    Megaphone,
    Menu,
    ReceiptText,
    ScrollText,
    ShieldCheck,
    UserRound,
    Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { useI18n, useMessages } from '../i18n';
import { usePageTitle } from '../lib/page-title';
import { useOpenRequests } from '../lib/platform';
import { useLogout, useSession } from '../lib/session';
import { PlatformTag } from './PlatformTag';

/**
 * The menu, as data, grouped by the job it belongs to as the seller panel's is
 * (docs/11 §8): the sellers on the platform, what they pay for it, and running
 * the platform itself.
 *
 * People, the audit log, revenue, plans and announcements are a level above
 * knowing which accounts exist, and staff are a superadmin's: each route names
 * the level it needs (`staticData.level` in main.tsx), and an entry is offered
 * only to an operator who has it; a group left with nothing renders nothing.
 * Hiding a link is not the access control — the API refuses regardless, and the
 * shell refuses the page — but offering a screen that always answers 403 is a
 * defect either way.
 */
const navGroups = [
    {
        titleKey: null,
        items: [{ to: '/', labelKey: 'overview', icon: House }],
    },
    {
        titleKey: 'sellers',
        items: [
            { to: '/hesaplar', labelKey: 'accounts', icon: Building },
            { to: '/kullanicilar', labelKey: 'users', icon: Users },
            { to: '/talepler', labelKey: 'requests', icon: Inbox },
            { to: '/duyurular', labelKey: 'announcements', icon: Megaphone },
        ],
    },
    {
        titleKey: 'subscriptions',
        items: [
            { to: '/gelir', labelKey: 'revenue', icon: ChartColumn },
            { to: '/odemeler', labelKey: 'payments', icon: ReceiptText },
            { to: '/planlar', labelKey: 'plans', icon: CreditCard },
            { to: '/odeme-ayarlari', labelKey: 'paymentSettings', icon: Landmark },
            { to: '/fatura-ayarlari', labelKey: 'invoiceSettings', icon: FileText },
        ],
    },
    {
        titleKey: 'administration',
        items: [
            { to: '/sistem', labelKey: 'system', icon: Activity },
            { to: '/denetim', labelKey: 'audit', icon: ScrollText },
            { to: '/personel', labelKey: 'staff', icon: ShieldCheck },
        ],
    },
] as const;

/** The operator's own account, beside signing out rather than in the menu's groups. */
const accountItem = { to: '/hesabim', labelKey: 'account', icon: UserRound } as const;

type NavPath = (typeof navGroups)[number]['items'][number]['to'] | (typeof accountItem)['to'];

/** Whether the operator meets the level a route names; a route with none is open to all staff. */
function useCanOpen() {
    const router = useRouter();
    const { atLeast } = useSession();
    return (to: NavPath) => {
        const level = router.routesByPath[to].options.staticData?.level;
        return level === undefined || atLeast(level);
    };
}

/**
 * The menu and signing out. Rendered twice — in the sidebar and in the phone
 * drawer — so each copy scrolls its own active entry into view.
 */
function Navigation() {
    const t = useMessages();
    const canOpen = useCanOpen();
    const logout = useLogout();
    const openRequests = useOpenRequests();
    const waiting = openRequests.data?.open ?? 0;
    const pathname = useRouterState({ select: (state) => state.location.pathname });

    async function onSignOut() {
        try {
            await logout.mutateAsync();
        } catch {
            // Leaving is what the operator asked for, so a failed request does not keep them here.
        }
        // A full navigation rather than a client-side one: signing out clears the
        // cache, and reloading guarantees nothing from the session survives in
        // memory on what is often a shared machine.
        window.location.assign('/giris');
    }

    return (
        <>
            <SidebarNav label={t.nav.menu} path={pathname}>
                {navGroups.map((group) => {
                    const items = group.items.filter((item) => canOpen(item.to));
                    if (items.length === 0) return null;
                    return (
                        <SidebarGroup key={group.titleKey ?? 'home'} title={group.titleKey && t.navGroups[group.titleKey]}>
                            {items.map((item) => (
                                <SidebarItem
                                    key={item.to}
                                    icon={<item.icon aria-hidden="true" />}
                                    badge={
                                        // A number, not a dot: the question on the operator's mind is how many.
                                        item.to === '/talepler' &&
                                        waiting > 0 && (
                                            <span className="block min-w-5 rounded-full bg-primary px-1 text-center text-12/5 font-semibold text-primary-foreground tabular-nums">
                                                {waiting}
                                            </span>
                                        )
                                    }
                                >
                                    <Link to={item.to}>{t.nav[item.labelKey]}</Link>
                                </SidebarItem>
                            ))}
                        </SidebarGroup>
                    );
                })}
            </SidebarNav>

            {/* The operator's two account actions keep their labels, one under the
                other: there is no row of others to fit, as there is in the seller panel. */}
            <SidebarFooter>
                <div className="flex w-full flex-col gap-1">
                    {canOpen(accountItem.to) && (
                        <SidebarItem icon={<accountItem.icon aria-hidden="true" />}>
                            <Link to={accountItem.to}>{t.nav[accountItem.labelKey]}</Link>
                        </SidebarItem>
                    )}
                    <SidebarItem icon={<LogOut aria-hidden="true" />}>
                        <button type="button" onClick={onSignOut} disabled={logout.isPending}>
                            {t.signOut}
                        </button>
                    </SidebarItem>
                </div>
            </SidebarFooter>
        </>
    );
}

function LanguageSelect() {
    const t = useMessages();
    const { locale, setLocale } = useI18n();
    return (
        <Select
            size="sm"
            className="w-auto min-w-32"
            value={locale}
            onChange={(event) => setLocale(event.target.value as 'tr' | 'en')}
            aria-label={t.common.language}
        >
            <option value="tr">Türkçe</option>
            <option value="en">English</option>
        </Select>
    );
}

/**
 * The page for the route on screen, or the refusal when the operator is below the
 * level the route names. A refused page is never mounted, so none of its requests
 * leave and none of its controls are drawn.
 */
function RoutedPage() {
    const { atLeast } = useSession();
    const refused = useMatches({
        select: (matches) =>
            matches.some((match) => {
                const level = match.staticData.level;
                return level !== undefined && !atLeast(level);
            }),
    });

    return refused ? <Refusal /> : <Outlet />;
}

/**
 * Stands in for a refused page, and names the browser tab for itself. A component
 * of its own rather than a branch of RoutedPage: the page under it names the tab
 * too, and a parent's effect would run after the child's and overwrite it.
 */
function Refusal() {
    const t = useMessages();
    usePageTitle(t.common.forbidden.title);
    return (
        <EmptyState
            titleAs="h1"
            icon={<Lock aria-hidden="true" />}
            title={t.common.forbidden.title}
            description={t.common.forbidden.body}
            action={
                <Button asChild variant="secondary">
                    <Link to="/">{t.common.forbidden.home}</Link>
                </Button>
            }
        />
    );
}

/**
 * The platform chrome: the seller panel's menu and top bar, so the two read as
 * one product. What tells them apart is the tag under the logo and the
 * operator's own level, shown next to their name permanently. Someone with
 * support access and someone who can enter any account on the platform use the
 * same screens, and the difference between them is the most important thing on
 * this page.
 */
export function AppShell() {
    const t = useMessages();
    const { me } = useSession();

    const level = me?.platformLevel as keyof typeof t.staff.level | undefined;

    // The phone menu: the same navigation, in a drawer from the left. It closes
    // on every route change so a tap on a link is one tap, not two.
    const [menuOpen, setMenuOpen] = useState(false);
    const pathname = useRouterState({ select: (state) => state.location.pathname });
    useEffect(() => setMenuOpen(false), [pathname]);

    return (
        <div className="grid min-h-dvh lg:grid-cols-[15rem_minmax(0,1fr)]">
            <Sidebar>
                <SidebarBrand>
                    <Logo />
                    <PlatformTag />
                </SidebarBrand>
                <Navigation />
            </Sidebar>

            <Drawer open={menuOpen} onClose={() => setMenuOpen(false)} side="left" title={<Logo />} closeLabel={t.common.close}>
                <div className="-mx-3 -my-6 flex min-h-full flex-col px-3 py-2">
                    <Navigation />
                    <div className="mt-3 flex gap-2 border-t border-border px-3 pt-3">
                        <LanguageSelect />
                        <ThemeToggle labels={t.common.theme} />
                    </div>
                </div>
            </Drawer>

            <div className="flex min-w-0 flex-col bg-muted">
                <header className="sticky top-0 z-20 flex h-14 items-center gap-4 border-b border-border bg-card px-6 max-lg:px-4">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="-ms-2 lg:hidden"
                        aria-label={t.nav.menu}
                        aria-expanded={menuOpen}
                        onClick={() => setMenuOpen(true)}
                    >
                        <Menu aria-hidden="true" />
                    </Button>

                    <div className="me-auto flex min-w-0 items-center gap-2 text-13">
                        <strong className="truncate font-semibold">{me?.user.name}</strong>
                        {level && <Badge tone="accent">{t.staff.level[level]}</Badge>}
                    </div>

                    {/* Language and theme live in the menu drawer on a phone. */}
                    <div className="hidden items-center gap-2 lg:flex">
                        <LanguageSelect />
                        <ThemeToggle labels={t.common.theme} />
                    </div>
                </header>

                <main className="min-w-0 p-6 max-lg:p-4">
                    <RoutedPage />
                </main>
            </div>
        </div>
    );
}
